import React from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PrayerForm from "@/components/PrayerForm";
import BookingInquiryForm from "@/components/BookingInquiryForm";
import SEOHead from "@/components/SEOHead";

const BookIeshia: React.FC = () => {
  const teachingTopics = [
    "Going from burnout to becoming",
    "Healing, identity, and purpose",
    "Faith and emotional wholeness",
    "Motherhood and maintaining self",
    "Testimony, transformation, and becoming",
    "Moving from survival mode into intentional living",
  ];

  return (
    <>
      <SEOHead
        title="Book Ieshia Danielle: Speaking, Events & Podcast Appearances"
        description="Bring Ieshia Danielle into the room. Speaking, moderation, and guest appearances on burnout, identity, faith, and moving from survival mode into intentional living."
        canonical="https://iamieshiadanielle.com/book"
        ogImage="https://assets.cdn.filesafe.space/N5B75kjUT85NEZBh2RaZ/media/6a9462bea90361de8b51948f.jpg"
        schemaJson={[
          {
            "@type": "WebPage",
            "@id": "https://iamieshiadanielle.com/book#webpage",
            url: "https://iamieshiadanielle.com/book",
            name: "Book Ieshia Danielle: Speaking, Events & Podcast Appearances",
            isPartOf: { "@id": "https://iamieshiadanielle.com/#website" },
            about: { "@id": "https://iamieshiadanielle.com/#person" },
            inLanguage: "en-US",
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://iamieshiadanielle.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Booking",
                item: "https://iamieshiadanielle.com/book",
              },
            ],
          },
          {
            "@type": "Service",
            "@id": "https://iamieshiadanielle.com/book#service",
            name: "Speaking & Appearances with Ieshia Danielle",
            serviceType:
              "Speaking engagements, podcast guest appearances, event moderation",
            provider: { "@id": "https://iamieshiadanielle.com/#person" },
            areaServed: "US",
            availableChannel: {
              "@type": "ServiceChannel",
              name: "In-person or virtual",
            },
            description:
              "Messages that meet the woman behind the responsibility: burnout to becoming, healing, identity and purpose, faith and emotional wholeness, motherhood and maintaining self, and moving from survival mode into intentional living. Inquiries answered within two business days.",
          },
        ]}
      />

      <div className="min-h-screen bg-[#FAF6F0] text-[#380C1E] flex flex-col font-sans">
        <Header />

        <main className="flex-1">
          {/* Hero */}
          <section className="bg-[#F5E9EC] py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              <div className="lg:col-span-6 space-y-5">
                <p className="text-xs font-semibold text-[#380C1E]/60 uppercase tracking-[0.2em] not-italic font-sans">
                  Minister · Teacher · Stewardship Coach
                </p>
                <h1 className="font-serif sm:text-5xl font-normal text-[#380C1E] leading-[1.15] text-2xl">
                  Bring Ieshia into the room where women are ready to stop
                  surviving and start becoming.
                </h1>
                <p className="text-sm md:text-base text-[#380C1E]/70 font-light leading-relaxed max-w-lg">
                  Honest, Scripture-rooted messages that help women name what is
                  draining them and leave with practical next steps for the life
                  God is calling them to lead.
                </p>

                <div className="pt-2">
                  <a
                    href="#booking-form"
                    className="bg-[#9E1B50] hover:bg-[#801340] text-white font-bold uppercase text-[11px] tracking-wider px-8 py-4 rounded-full inline-flex items-center gap-2 transition-all"
                  >
                    Invite Ieshia to speak →
                  </a>
                </div>
              </div>

              <div className="lg:col-span-6">
                <div className="rounded-[2rem] overflow-hidden bg-neutral-200 border border-white/40 shadow-md aspect-square">
                  <img
                    src="https://assets.cdn.filesafe.space/N5B75kjUT85NEZBh2RaZ/media/6a9462bea90361de8b51948f.jpg"
                    alt="Ieshia Danielle speaking on stage"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Teaching Concepts */}
          <section className="py-10 md:py-14 bg-card">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
              <div className="text-center max-w-3xl mx-auto space-y-2">
                <p className="text-[10px] font-semibold text-[#9E1B50] uppercase tracking-widest italic">
                  Teaching concepts
                </p>
                <h2 className="font-serif text-2xl md:text-3xl font-normal text-[#380C1E]">
                  Messages that meet the woman behind the responsibility.
                </h2>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                {teachingTopics.map((topic, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-2xl border border-[#E8DEC8] p-3 md:p-4 space-y-1.5 shadow-sm"
                  >
                    <span className="text-[9px] font-bold text-[#9E1B50] uppercase tracking-widest">
                      Topic 0{i + 1}
                    </span>
                    <p className="font-serif text-sm md:text-base font-normal text-[#380C1E] leading-snug">
                      {topic}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Teaching Concept */}
          <section className="bg-[#4A0E26] py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10 lg:space-y-14">
              <div className="relative rounded-[2rem] overflow-hidden bg-black/30 border border-white/20 shadow-lg aspect-[9/16] max-w-sm mx-auto">
                <video
                  src="https://assets.cdn.filesafe.space/N5B75kjUT85NEZBh2RaZ/media/6a95be40e68555d6d5ef0fa5.mp4#t=0.1"
                  controls
                  playsInline
                  preload="metadata"
                  aria-label="Ieshia Danielle speaking at the Style Evolved event"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>

              <div className="max-w-2xl mx-auto text-center space-y-5">
                <p className="text-xs font-semibold text-white/70 uppercase tracking-widest italic">
                  Featured Teaching Concept
                </p>
                <h2 className="font-serif text-3xl md:text-5xl font-normal text-white leading-[1.15]">
                  Pain does not just hurt you. It teaches you.
                </h2>
                <p className="text-white/80 font-light text-base leading-relaxed">
                  From the Style Evolved Workshop: the pain you survive can
                  quietly become the teacher shaping how you dress, decide,
                  relate, hide, and lead. Healing begins when you identify the
                  lesson pain taught you and decide whether it is still true.
                </p>
              </div>
            </div>
          </section>

          {/* What Happens in the Room */}
          <section className="bg-white py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              <div className="lg:col-span-5">
                <div className="rounded-[2rem] overflow-hidden bg-neutral-200 border border-[#E8DEC8] shadow-md aspect-[16/9] max-w-xl">
                  <img
                    src="https://assets.cdn.filesafe.space/N5B75kjUT85NEZBh2RaZ/media/6a9462bd7fb05fdeb8ff0853.jpg"
                    alt="Ieshia Danielle in the room with women at a live event"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <p className="mt-2 text-[10px] text-[#380C1E]/50 font-light text-center max-w-xl">
                  Style Evolved Summit, hosted by Ceceily Maraya.
                </p>
              </div>

              <div className="lg:col-span-7 space-y-5">
                <p className="text-xs font-semibold text-[#9E1B50] uppercase tracking-widest italic">
                  What happens in the room
                </p>
                <h2 className="font-serif text-3xl md:text-5xl font-normal text-[#380C1E] leading-[1.15]">
                  Women feel seen, tell the truth, and leave with language for
                  what comes next.
                </h2>
                <p className="text-[#380C1E]/70 text-sm md:text-base font-light leading-relaxed max-w-xl">
                  Ieshia pairs lived testimony and Biblical truth with the
                  practical reflection women need to move. The message is honest
                  enough to open the room and clear enough to carry home.
                </p>
              </div>
            </div>
          </section>

          <BookingInquiryForm />

          <PrayerForm />
        </main>

        <Footer />
      </div>
    </>
  );
};

export default BookIeshia;
